import { c as _c } from "react/compiler-runtime";
import { FaroTextField } from "@components/common/faro-text-field/faro-text-field";
import { InfoBar } from "@components/common/info-bar/info-bar";
import { InfoBarPerson } from "@components/common/info-bar/info-bar-person";
import { InfoBarValue } from "@components/common/info-bar/info-bar-value";
import { useHasUserValidFeatureCompanyLevel } from "@hooks/feature-control/use-has-user-valid-feature-company-level";
import { ChangeManagementTrigger } from "@pages/project-details/project-overview/change-management/change-management-trigger";
import { fetchingProjectsFlagsSelector } from "@store/projects/projects-selector";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { GroupMembersProvider } from "@context-providers/group-members-provider";
import { selectedProjectManagerSelector } from "@store/projects/projects-selector";
import { updateProjectDetails } from "@store/projects/projects-slice-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { MIN_PROJECT_NAME_LENGTH } from "@utils/project-utils";
import { ChangeProjectDetailsEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
/**
 * Shows the main details for a project. These include the project name, manager, group and access level
 * They are displayed at the top of the page, inside a gray box
 */
export function MainProjectDetails(t0) {
  const $ = _c(29);
  const {
    isProjectEditable,
    project,
    isLoading,
    isDescribeOnly: t1
  } = t0;
  const isDescribeOnly = t1 === undefined ? false : t1;
  const dispatch = useAppDispatch();
  const selectedProjectManager = useAppSelector(selectedProjectManagerSelector);
  const {
    isUpdatingProjects
  } = useAppSelector(fetchingProjectsFlagsSelector);
  const {
    canAssignManagers
  } = useHasUserValidFeatureCompanyLevel();
  const {
    handleErrorWithToast
  } = useErrorContext();
  const {
    trackEvent
  } = useTrackEvent();
  let t2;
  if ($[0] !== dispatch || $[1] !== handleErrorWithToast || $[2] !== project?.name || $[3] !== trackEvent) {
    t2 = async function onUpdateName(value) {
      trackEvent({
        name: ChangeProjectDetailsEvents.changeProjectName,
        props: {
          newNameLength: value.length
        }
      });
      ;
      try {
        await dispatch(updateProjectDetails({
          payload: {
            name: value
          }
        }));
        return value;
      } catch (t3) {
        const error = t3;
        handleErrorWithToast({
          id: `changeName-${Date.now().toString()}`,
          title: "Could not change name",
          error
        });
        return project?.name ?? "";
      }
    };
    $[0] = dispatch;
    $[1] = handleErrorWithToast;
    $[2] = project?.name;
    $[3] = trackEvent;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const onUpdateName = t2;
  if (isLoading || !project) {
    let t3;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <InfoBar isLoading={true} />;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    return t3;
  }
  const t3 = !isProjectEditable;
  let t4;
  if ($[6] !== onUpdateName || $[7] !== project.name || $[8] !== t3) {
    t4 = {
      label: "Project",
      isFlexible: true,
      content: <FaroTextField ariaLabel="Project" initialValue={project.name} faroVariant="main" fontSize="1.125rem" isReadOnly={t3} shouldShowTooltip={true} minInputLength={MIN_PROJECT_NAME_LENGTH} onConfirmed={onUpdateName} />
    };
    $[6] = onUpdateName;
    $[7] = project.name;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const t5 = project.group?.name ?? "No Group";
  let t6;
  if ($[10] !== t5) {
    t6 = <ChangeManagementTrigger ariaLabel="Change group"><InfoBarValue>{t5}</InfoBarValue></ChangeManagementTrigger>;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== isDescribeOnly || $[13] !== t6) {
    t7 = {
      label: "Group",
      isFlexible: true,
      content: t6,
      isHidden: isDescribeOnly
    };
    $[12] = isDescribeOnly;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== isUpdatingProjects || $[16] !== selectedProjectManager.name || $[17] !== selectedProjectManager.user) {
    t8 = <ChangeManagementTrigger ariaLabel="Change project manager"><InfoBarPerson member={selectedProjectManager.user} name={selectedProjectManager.name} isLoading={isUpdatingProjects} /></ChangeManagementTrigger>;
    $[15] = isUpdatingProjects;
    $[16] = selectedProjectManager.name;
    $[17] = selectedProjectManager.user;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const t9 = isDescribeOnly || !canAssignManagers;
  let t10;
  if ($[19] !== t8 || $[20] !== t9) {
    t10 = {
      label: "Project Manager",
      isFlexible: true,
      content: t8,
      isHidden: t9
    };
    $[19] = t8;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t10 || $[23] !== t4 || $[24] !== t7) {
    t11 = <InfoBar items={[t4, t7, t10]} />;
    $[22] = t10;
    $[23] = t4;
    $[24] = t7;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== project || $[27] !== t11) {
    t12 = <GroupMembersProvider project={project}>{t11}</GroupMembersProvider>;
    $[26] = project;
    $[27] = t11;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  return t12;
}